import { useEffect, useRef, useState } from "react";

const COL_A = [
  { name: "Adobe (Ps, Ai, Id, Acrobat)", value:85 },
  { name: "Affinity (Designer, Photo)", value: 85 },
  { name: "Google Analytics / GA4", value: 75 },
  { name: "Meta Business Manager", value: 85 },
];

const COL_B = [
  { name: "CMS (Tilda, WordPress)", value: 85 },
  { name: "Newsletter (Ecomail, Brevo)", value: 85 },
  { name: "MS Office", value: 85 },
  { name: "CRM (Raynet)", value: 60 },
];

function SkillBar({ name, value, delay = 0, testIdIndex }) {
  const ref = useRef(null);
  const [active, setActive] = useState(false);
  const [shown, setShown] = useState(0);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActive(true);
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.35 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!active) return;
    let raf;
    const start = performance.now() + delay;
    const dur = 1200;
    const tick = (now) => {
      const t = Math.max(0, Math.min(1, (now - start) / dur));
      const eased = 1 - Math.pow(1 - t, 3);
      setShown(Math.round(eased * value));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, delay, value]);

  return (
    <div ref={ref} data-testid={`skill-bar-${testIdIndex}`} className="group">
      <div className="flex items-baseline justify-between mb-2.5">
        <span className="text-sm lg:text-base font-medium text-white/90">
          {name}
        </span>
        <span className="font-mono text-[12px] text-[#C6F432] tabular-nums">
          {shown}%
        </span>
      </div>
      <div className="h-2 w-full rounded-full bg-white/[0.05] border border-white/[0.06] overflow-hidden">
        <div
          className="h-full rounded-full bg-[#C6F432] transition-[width] duration-[1200ms] ease-out shadow-[0_0_18px_rgba(198,244,50,0.45)]"
          style={{
            width: active ? `${value}%` : "0%",
            transitionDelay: `${delay}ms`,
          }}
        />
      </div>
    </div>
  );
}

export default function Skills() {
  return (
    <section
      id="nastroje"
      data-testid="skills-section"
      className="relative py-24 lg:py-32 border-t border-white/[0.06]"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
          <div>
            <span className="pill-tag" data-testid="skills-pill">
              <span className="pill-dot"></span>
              05 — Nástroje, ktoré ovládam
            </span>
            <h2 className="mt-6 text-4xl lg:text-6xl font-extrabold tracking-tight leading-[1.02] max-w-2xl">
              Stack, na ktorý
              <br />
              <span className="text-[#C6F432]">sa môžete spoľahnúť.</span>
            </h2>
          </div>
          <p className="max-w-md text-white/55 text-base lg:text-lg">
            Od kreatívneho softvéru po analytické platformy a CMS — všetko,
            čo potrebuje moderný marketingový tím.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 lg:gap-x-20 gap-y-7">
          <div className="space-y-7">
            {COL_A.map((s, i) => (
              <SkillBar
                key={s.name}
                name={s.name}
                value={s.value}
                delay={i * 120}
                testIdIndex={`a-${i}`}
              />
            ))}
          </div>
          <div className="space-y-7">
            {COL_B.map((s, i) => (
              <SkillBar
                key={s.name}
                name={s.name}
                value={s.value}
                delay={i * 120}
                testIdIndex={`b-${i}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
